import { useId, useState } from "react"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import { JsonCodeEditor } from "@/components/JsonCodeEditor"
import { FieldLabel } from "@/components/shared/forms/FieldLabel"
import { PhoneNumberInput } from "@/components/shared/forms/PhoneNumberInput"
import { SipAddressInput } from "@/components/shared/forms/SipAddressInput"
import { SipAdvancedFields } from "@/components/shared/forms/SipAdvancedFields"
import { useClearMediaEncryptionOutsideSipMode } from "@/hooks/useClearMediaEncryptionOutsideSipMode"
import { useConnectionMode } from "@/hooks/useConnectionMode"
import { useDispatchMode, type DispatchMode } from "@/hooks/useDispatchMode"
import { useLiveKitAdvancedConfig } from "@/hooks/useLiveKitAdvancedConfig"
import { isValidSipAddress } from "@/utils/persona/phoneNumberUtils"

interface BaseFormFieldProps {
  disabled?: boolean
  className?: string
}

/**
 * Info-tooltip copy for inbound voice agent arguments. Centralized so the
 * single- and multi-phone inbound field variants stay in sync.
 */
export const INBOUND_VOICE_TOOLTIPS = {
  connectionType:
    "How Coval connects to your inbound agent: a phone number (PSTN) or a SIP address for direct SIP trunking.",
  phoneNumber:
    "The phone number Coval dials to reach your inbound agent, in E.164 format (e.g. +12345678901).",
  sipAddress:
    "The SIP address Coval dials to reach your inbound agent (e.g. sip:agent@example.com).",
} as const

export const SMS_TOOLTIPS = {
  phoneNumber:
    "The phone number Coval texts to reach your SMS agent, in E.164 format (e.g. +14155551234).",
} as const

/**
 * Info-tooltip copy for LiveKit agent arguments. Mirrors
 * INBOUND_VOICE_TOOLTIPS so all agent forms read consistently.
 */
const LIVEKIT_TOOLTIPS = {
  tokenEndpoint:
    "Endpoint Coval sends a POST request to (with room_name and participant_name) to generate a LiveKit access token. Must be a valid HTTPS URL.",
  contentType:
    "The Content-Type header Coval sends on the token-generation request (e.g. application/json).",
  livekitUrl:
    "Your LiveKit server WebSocket URL (wss://…). If your token endpoint returns serverUrl, that value overrides this.",
  dispatchMode:
    "How your LiveKit agent joins the room. Automatic dispatch: your worker has no agent_name and auto-joins every new room — most setups use this by default. Explicit dispatch: your worker registers with an agent_name and only joins when the access token requests that same name.",
  sandboxId:
    "LiveKit's Sandbox and newer Development Token Server are the same underlying mechanism under two different names (LiveKit's own SDKs still call this parameter sandboxId internally). Useful for development/testing — e.g. quickly trying out a LiveKit agent without running your own token server. Not needed if you have a real production token endpoint. Leave empty if you're not using LiveKit's hosted sandbox/dev-token-server.",
  agentName:
    "Must match the agent_name your LiveKit agent worker registered under (via WorkerOptions in LiveKit's agents SDK) — not this Coval agent's own name above.",
  customHeaders:
    "Optional HTTP headers (JSON) sent with the token-generation request, e.g. an Authorization bearer token. Supports {{simulation_output_id}} (alias: {{simulation_id}}), {{test_case.*}}, {{agent.*}}, and {{run_metadata.*}}.",
  customPayload:
    "Optional JSON fields merged with room_name and participant_name when Coval calls your token endpoint. Supports {{simulation_output_id}} (alias: {{simulation_id}}), {{test_case.*}}, {{agent.*}}, and {{run_metadata.*}}.",
} as const

interface PhoneNumberFieldProps extends BaseFormFieldProps {
  value: string
  onChange: (value: string) => void
  labelTooltip?: React.ReactNode
  hidePlaceholder?: boolean
}

export const PhoneNumberField: React.FC<PhoneNumberFieldProps> = ({
  value,
  onChange,
  disabled,
  labelTooltip,
  hidePlaceholder,
}) => {
  return (
    <div className="space-y-2">
      <FieldLabel required tooltip={labelTooltip}>
        Phone Number
      </FieldLabel>
      <PhoneNumberInput
        value={value}
        onChange={onChange}
        disabled={disabled}
        hidePlaceholder={hidePlaceholder}
      />
    </div>
  )
}

interface SipAddressFieldProps extends BaseFormFieldProps {
  value: string
  onChange: (value: string) => void
  labelTooltip?: React.ReactNode
  hidePlaceholder?: boolean
}

export const SipAddressField: React.FC<SipAddressFieldProps> = ({
  value,
  onChange,
  disabled,
  className = "",
  labelTooltip,
  hidePlaceholder,
}) => {
  return (
    <div className="space-y-2">
      <FieldLabel required tooltip={labelTooltip}>
        SIP Address
      </FieldLabel>
      <SipAddressInput
        hidePlaceholder={hidePlaceholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className={className}
        error={value && !isValidSipAddress(value) ? "invalid" : undefined}
      />
    </div>
  )
}

interface LiveKitFieldsProps extends BaseFormFieldProps {
  hidePlaceholders?: boolean
  hideAdvancedSettings?: boolean
  tokenEndpoint: string
  contentType: string
  livekitUrl: string
  agentName: string
  sandboxId: string
  customHeaders?: string
  customPayload?: string
  /**
   * The user's last dispatch-mode selection, persisted in
   * formData.metadata.livekit_dispatch_mode — a transient field stripped
   * before save (see getLiveKitMetadataForSave) so nothing new reaches the
   * backend. Passed through to useDispatchMode; see that hook for why this
   * exists instead of a useState local to this component.
   */
  dispatchModeSelection?: DispatchMode
  onTokenEndpointChange: (value: string) => void
  onContentTypeChange: (value: string) => void
  onLivekitUrlChange: (value: string) => void
  onAgentNameChange: (value: string) => void
  onSandboxIdChange: (value: string) => void
  onCustomHeadersChange?: (value: string) => void
  onCustomPayloadChange?: (value: string) => void
  onDispatchModeSelectionChange: (mode: DispatchMode) => void
}

interface LiveKitAdvancedFieldsProps extends BaseFormFieldProps {
  contentType?: string
  onContentTypeChange?: (value: string) => void
  sandboxId: string
  customHeaders?: string
  customPayload?: string
  onSandboxIdChange: (value: string) => void
  onCustomHeadersChange?: (value: string) => void
  onCustomPayloadChange?: (value: string) => void
  hidePlaceholders?: boolean
}

export const LiveKitAdvancedFields: React.FC<LiveKitAdvancedFieldsProps> = ({
  contentType,
  onContentTypeChange,
  sandboxId,
  customHeaders = "",
  customPayload = "",
  onSandboxIdChange,
  onCustomHeadersChange,
  onCustomPayloadChange,
  disabled,
  className = "",
  hidePlaceholders = false,
}) => {
  const contentTypeInputId = useId()
  const sandboxIdInputId = useId()
  const {
    localHeaders,
    localPayload,
    headersError,
    payloadError,
    handleHeadersChange,
    handlePayloadChange,
  } = useLiveKitAdvancedConfig({
    customHeaders,
    customPayload,
    onCustomHeadersChange,
    onCustomPayloadChange,
  })

  return (
    <div className="space-y-4">
      {onContentTypeChange && (
        <div className="space-y-2">
          <FieldLabel
            htmlFor={contentTypeInputId}
            tooltip={LIVEKIT_TOOLTIPS.contentType}
          >
            Content Type
          </FieldLabel>
          <Input
            id={contentTypeInputId}
            value={contentType || ""}
            onChange={(event) => onContentTypeChange(event.target.value)}
            placeholder="Enter the request content type"
            disabled={disabled}
            className={className}
          />
        </div>
      )}
      <div className="space-y-2">
        <FieldLabel
          htmlFor={sandboxIdInputId}
          tooltip={LIVEKIT_TOOLTIPS.sandboxId}
        >
          Sandbox / Dev Token Server ID
        </FieldLabel>
        <Input
          id={sandboxIdInputId}
          value={sandboxId}
          onChange={(event) => onSandboxIdChange(event.target.value)}
          className={className}
          disabled={disabled}
          placeholder={
            hidePlaceholders
              ? "Enter your sandbox token server ID"
              : "workshop-xyz"
          }
        />
      </div>
      <div className="space-y-2">
        <FieldLabel tooltip={LIVEKIT_TOOLTIPS.customHeaders}>
          Custom Headers
        </FieldLabel>
        <JsonCodeEditor
          hideGutters
          copyable
          value={localHeaders}
          onChange={handleHeadersChange}
          className={className}
          placeholder={
            '{\n  "X-API-Key": "your-api-key",\n  "X-Coval-Simulation-Id": "{{simulation_id}}"\n}'
          }
          rows={4}
          error={headersError}
          ariaLabel="Custom Headers JSON"
        />
      </div>
      <div className="space-y-2">
        <FieldLabel tooltip={LIVEKIT_TOOLTIPS.customPayload}>
          Custom Payload Fields
        </FieldLabel>
        <JsonCodeEditor
          hideGutters
          copyable
          value={localPayload}
          onChange={handlePayloadChange}
          className={className}
          placeholder={
            '{\n  "agent_variant": "maria_friendly",\n  "demo_type": "openai",\n  "room_config": {\n    "agents": [{\n      "agent_name": "my-agent"\n    }]\n  }\n}'
          }
          rows={6}
          error={payloadError}
          ariaLabel="Custom Payload Fields JSON"
        />
      </div>
    </div>
  )
}

export const LiveKitFields: React.FC<LiveKitFieldsProps> = ({
  hidePlaceholders = false,
  hideAdvancedSettings = false,
  tokenEndpoint,
  contentType,
  livekitUrl,
  agentName,
  sandboxId,
  customHeaders = "",
  customPayload = "",
  dispatchModeSelection,
  onTokenEndpointChange,
  onContentTypeChange,
  onLivekitUrlChange,
  onAgentNameChange,
  onSandboxIdChange,
  onCustomHeadersChange,
  onCustomPayloadChange,
  onDispatchModeSelectionChange,
  disabled,
  className = "",
}) => {
  const tokenEndpointId = useId()
  const contentTypeId = useId()
  const livekitUrlId = useId()
  const agentNameId = useId()
  const [showAdvanced, setShowAdvanced] = useState(false)
  const {
    inputMode: dispatchMode,
    handleModeChange: handleDispatchModeChange,
  } = useDispatchMode({
    agentName,
    selectedMode: dispatchModeSelection,
    onAgentNameChange,
    onSelectedModeChange: onDispatchModeSelectionChange,
  })

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <FieldLabel
          htmlFor={tokenEndpointId}
          required
          tooltip={LIVEKIT_TOOLTIPS.tokenEndpoint}
        >
          Token Generation Endpoint
        </FieldLabel>
        <Input
          id={tokenEndpointId}
          value={tokenEndpoint}
          onChange={(e) => onTokenEndpointChange(e.target.value)}
          className={className}
          disabled={disabled}
          placeholder={
            hidePlaceholders
              ? "Enter your token generation endpoint"
              : "https://cloud-api.livekit.io/api/sandbox/connection-details"
          }
        />
      </div>
      {!hideAdvancedSettings && (
        <div className="space-y-2">
          <FieldLabel
            htmlFor={contentTypeId}
            tooltip={LIVEKIT_TOOLTIPS.contentType}
          >
            Content Type
          </FieldLabel>
          <Input
            id={contentTypeId}
            value={contentType}
            onChange={(e) => onContentTypeChange(e.target.value)}
            className={className}
            disabled={disabled}
            placeholder={
              hidePlaceholders
                ? "Enter the request content type"
                : "application/json"
            }
          />
        </div>
      )}
      <div className="space-y-2">
        <FieldLabel
          htmlFor={livekitUrlId}
          required
          tooltip={LIVEKIT_TOOLTIPS.livekitUrl}
        >
          LiveKit URL
        </FieldLabel>
        <Input
          id={livekitUrlId}
          value={livekitUrl}
          onChange={(e) => onLivekitUrlChange(e.target.value)}
          className={className}
          disabled={disabled}
          placeholder={
            hidePlaceholders
              ? "Enter your LiveKit URL"
              : "wss://workshop-xyz.livekit.cloud"
          }
        />
      </div>
      <div className="space-y-2">
        <FieldLabel tooltip={LIVEKIT_TOOLTIPS.dispatchMode}>
          Dispatch Mode
        </FieldLabel>
        <Select
          value={dispatchMode}
          onValueChange={handleDispatchModeChange}
          disabled={disabled}
        >
          <SelectTrigger aria-label="Dispatch Mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="automatic">Automatic dispatch</SelectItem>
            <SelectItem value="explicit">Explicit dispatch</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {dispatchMode === "explicit" ? (
        <div className="space-y-2">
          <FieldLabel
            htmlFor={agentNameId}
            required
            tooltip={LIVEKIT_TOOLTIPS.agentName}
          >
            LiveKit Agent Name
          </FieldLabel>
          <Input
            id={agentNameId}
            value={agentName}
            placeholder="Enter the registered LiveKit agent name"
            onChange={(e) => onAgentNameChange(e.target.value)}
            className={className}
            disabled={disabled}
          />
        </div>
      ) : null}
      {!hideAdvancedSettings && (
        <Accordion
          type="single"
          collapsible
          className="border-t border-coval-border px-0 pt-4"
          value={showAdvanced ? "advanced" : ""}
          onValueChange={(value) => setShowAdvanced(value === "advanced")}
        >
          <AccordionItem value="advanced">
            <AccordionTrigger disabled={disabled}>
              Advanced Configuration
            </AccordionTrigger>
            <AccordionContent>
              <LiveKitAdvancedFields
                sandboxId={sandboxId}
                customHeaders={customHeaders}
                customPayload={customPayload}
                onSandboxIdChange={onSandboxIdChange}
                onCustomHeadersChange={onCustomHeadersChange}
                onCustomPayloadChange={onCustomPayloadChange}
                disabled={disabled}
                className={className}
                hidePlaceholders={hidePlaceholders}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  )
}

interface InboundVoiceFieldsProps extends BaseFormFieldProps {
  phoneNumber: string
  onPhoneNumberChange: (value: string) => void
  onConnectionModeChange?: (mode: "phone" | "sip") => void
  customSipHeaders?: string
  onCustomSipHeadersChange?: (value: string) => void
  mediaEncryption?: string
  onMediaEncryptionChange?: (value: string) => void
  hidePlaceholders?: boolean
  hideSipAdvancedFields?: boolean
}

export const InboundVoiceFields: React.FC<InboundVoiceFieldsProps> = ({
  phoneNumber,
  onPhoneNumberChange,
  onConnectionModeChange,
  customSipHeaders,
  onCustomSipHeadersChange,
  mediaEncryption,
  onMediaEncryptionChange,
  disabled,
  className = "",
  hidePlaceholders = false,
  hideSipAdvancedFields = false,
}) => {
  const connectionTypeId = useId()

  const { inputMode, handleModeChange } = useConnectionMode({
    phoneNumber,
    onPhoneNumberChange,
    onModeChange: onConnectionModeChange,
  })

  const isSipMode = inputMode === "sip"
  useClearMediaEncryptionOutsideSipMode(
    isSipMode,
    phoneNumber,
    mediaEncryption,
    onMediaEncryptionChange,
  )

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <FieldLabel
            htmlFor={connectionTypeId}
            tooltip={INBOUND_VOICE_TOOLTIPS.connectionType}
          >
            Connection Type
          </FieldLabel>
          <Select
            value={inputMode}
            onValueChange={(value) => handleModeChange(value)}
            disabled={disabled}
          >
            <SelectTrigger aria-label="Connection Type" id={connectionTypeId}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="phone">Phone Number</SelectItem>
              <SelectItem value="sip">SIP Address</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {inputMode === "phone" ? (
          <PhoneNumberField
            hidePlaceholder={hidePlaceholders}
            value={phoneNumber}
            onChange={onPhoneNumberChange}
            disabled={disabled}
            className={className}
            labelTooltip={INBOUND_VOICE_TOOLTIPS.phoneNumber}
          />
        ) : (
          <div className="space-y-2">
            <FieldLabel required tooltip={INBOUND_VOICE_TOOLTIPS.sipAddress}>
              SIP Address
            </FieldLabel>
            <SipAddressInput
              hidePlaceholder={hidePlaceholders}
              value={phoneNumber}
              onChange={onPhoneNumberChange}
              disabled={disabled}
              className={className}
              error={
                phoneNumber && !isValidSipAddress(phoneNumber)
                  ? "invalid"
                  : undefined
              }
            />
          </div>
        )}
      </div>
      {inputMode === "sip" && !hideSipAdvancedFields && (
        <SipAdvancedFields
          customSipHeaders={customSipHeaders}
          onCustomSipHeadersChange={onCustomSipHeadersChange}
          mediaEncryption={mediaEncryption}
          onMediaEncryptionChange={onMediaEncryptionChange}
          disabled={disabled}
        />
      )}
    </div>
  )
}

export {
  DeepgramRealtimeFields,
  GeminiRealtimeFields,
  GrokRealtimeFields,
  OpenAIRealtimeFields,
} from "./RealtimeSimulatorFormFields"
